// Call reasons and the booking rate on the office screens (BL-003): what they say and decide, kept
// out of the components so they are tested without a browser. The server classifies (ServiceTitan's
// rules) and words every refusal; this lists, picks and words a rate.

import { longDay } from '../records/records-model.ts';

/** callReasons.list: one reason a call didn't book. */
export interface CallReason {
  id: string;
  name: string;
  isLead: boolean;
  excused: boolean;
  active: boolean;
  sort: number;
  standard: boolean;
  rowVersion: number;
}

/** calls.bookingRate: one row (a person, a reason or a campaign). */
export interface RateRow {
  key: string | null;
  label: string;
  taken: number;
  booked: number;
  leads: number;
  unbookedLeads: number;
  excused: number;
  underAMinute: number;
  notSet: number;
  changedInReview: number;
  rate: number | null;
}

/** Who reviews a call's reason and sees everyone's rate (calls.review): owner and managers. */
export const REVIEW_PERMS = ['calls.review'];
export const reviewsCalls = (held: readonly string[]) => REVIEW_PERMS.some((p) => held.includes(p));

/** "75%" or "—" (nothing to count yet). */
export const ratePct = (rate: number | null) => (rate === null ? '—' : `${Math.round(rate * 100)}%`);

/** "6 of 8 leads booked (75%)", or what there is when there are no leads. */
export function rateLine(r: Pick<RateRow, 'booked' | 'unbookedLeads' | 'rate'>): string {
  const counted = r.booked + r.unbookedLeads;
  if (!counted) return 'No leads yet';
  return `${r.booked} of ${counted} ${counted === 1 ? 'lead' : 'leads'} booked (${ratePct(r.rate)})`;
}

/** What a reason means for the rate, in words: shown beside it where it's chosen or edited. */
export function reasonMeans(r: Pick<CallReason, 'isLead' | 'excused'>): string {
  if (r.isLead && r.excused) return 'A lead, excused';
  if (r.isLead) return 'A lead: counts against the booking rate';
  return r.excused ? 'Not a lead, excused' : 'Not a lead';
}

/** The ranges the booking rate is read over: this week (from Monday), this month, last month. */
export type RangeKey = 'week' | 'month' | 'last_month';
export function rangeOf(key: RangeKey, today: string): { from: string; to: string } {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const day = (dt: Date) => dt.toISOString().slice(0, 10);
  if (key === 'week') {
    const t = new Date(Date.UTC(y, m - 1, d));
    const back = (t.getUTCDay() + 6) % 7; // Monday
    return { from: day(new Date(Date.UTC(y, m - 1, d - back))), to: today };
  }
  if (key === 'month') return { from: `${today.slice(0, 8)}01`, to: today };
  return { from: day(new Date(Date.UTC(y, m - 2, 1))), to: day(new Date(Date.UTC(y, m - 1, 0))) };
}

/**
 * Calls from before Flowology kept who took them have no taker (actions' review, L1: no backfill),
 * so a range reaching back before that day says what the people rows count.
 */
export function takersNote(
  from: string,
  takersSince: string | null,
  rows: readonly Pick<RateRow, 'key'>[],
): string | null {
  if (!takersSince || from >= takersSince || !rows.some((r) => r.key === null)) return null;
  return `Counts calls from ${longDay(takersSince)} and imported history.`;
}

/**
 * Which action records the office's own part of a call (reason, notes, who took it, a claim, the
 * later call it was handled on). While the company mirrors ServiceTitan the call's outcome and
 * customer are ServiceTitan's, so calls.update is refused and calls.setReason writes only ours.
 */
export const callWrite = (mirroring: boolean): 'calls.update' | 'calls.setReason' =>
  mirroring ? 'calls.setReason' : 'calls.update';
